import { isBooleanTrue, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { convertToSlug, toE164PhoneNumber } from "../utils/stringFormatter";
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';
import moment from "moment-timezone";
import { ref, uploadBytes } from "firebase/storage";
import { v4 as uuid } from 'uuid';
import { toLower } from "lodash";

class Partner {
    static collectionName = 'partners';

    name = null;
    slug = convertToSlug(this.name);
    partnerType = null;
    legalName = null; // legal business name
    active = false;

    defaultCouponCode = null; // if they have a default coupon code that should be applied to all conversions associated with them

    #image = null;
    imageRefId = null;

    // MAY WANT TO STORE w9 in a separate collection if we want to restrict access to it more, but for now will just store the fileRefId here with the rest of the partner info since we are restricting access to the entire partner record in general and only users with admin access can see the w9 file
    #file = null;
    w9FileRefId = null; // their W9 file on record

    // Partner could subsidize or fully pay for products for their clients (or employees in the case of enterprise employers)

    // could partner have multiple locations such as a franchise? how to handle this?

    // should there be a way to say they want their discount to go to the customer instead of their commission?

    billingAddress = null;
    shippingAddress = null;

    phone = null;
    fax = null; // new
    email = null; // new

    privacyPolicy = null;
    consentDocument = null;

    storeId = null;
    refId = null;

    constructor(obj) {
        if (isObjectNotEmpty(obj)) {
            if (isStringNotEmpty(obj.name)) { this.name = obj.name; }
            if (isStringNotEmpty(obj.slug)) { this.slug = obj.slug; }
            else if (isStringNotEmpty(obj.name)) { this.slug = convertToSlug(obj.name); }
            if (isStringNotEmpty(obj.partnerType)) { this.partnerType = obj.partnerType; }
            if (isStringNotEmpty(obj.legalName)) { this.legalName = obj.legalName; }
            if (isBooleanTrue(obj.active)) { this.active = true; }

            if (isStringNotEmpty(obj.defaultCouponCode)) { this.defaultCouponCode = obj.defaultCouponCode; }

            if (isNotNullOrUndefined(obj.image)) { this.#image = obj.image; }
            if (isStringNotEmpty(obj.imageRefId)) { this.imageRefId = obj.imageRefId; }

            if (isNotNullOrUndefined(obj.file)) { this.#file = obj.file; }
            if (isStringNotEmpty(obj.w9FileRefId)) { this.w9FileRefId = obj.w9FileRefId; }

            if (isObjectNotEmpty(obj.billingAddress)) { this.billingAddress = obj.billingAddress; }
            if (isObjectNotEmpty(obj.shippingAddress)) { this.shippingAddress = obj.shippingAddress; }

            if (isStringNotEmpty(obj.phone)) { this.phone = toE164PhoneNumber(obj.phone); }
            if (isStringNotEmpty(obj.fax)) { this.fax = toE164PhoneNumber(obj.fax); }
            if (isStringNotEmpty(obj.email)) { this.email = toLower(obj.email); }

            if (isStringNotEmpty(obj.privacyPolicy)) { this.privacyPolicy = obj.privacyPolicy; }
            if (isStringNotEmpty(obj.consentDocument)) { this.consentDocument = obj.consentDocument; }

            if (isStringNotEmpty(obj.storeId)) { this.storeId = obj.storeId; }
            if (isStringNotEmpty(obj.refId)) { this.refId = obj.refId; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            slug: this.slug,
            partnerType: this.partnerType,
            legalName: this.legalName,
            active: this.active,

            defaultCouponCode: this.defaultCouponCode,

            imageRefId: this.imageRefId,

            w9FileRefId: this.w9FileRefId,

            shippingAddress: this.shippingAddress,
            billingAddress: this.billingAddress,

            phone: this.phone,
            fax: this.fax,
            email: this.email,

            privacyPolicy: this.privacyPolicy,
            consentDocument: this.consentDocument,

            storeId: this.storeId,
            refId: this.refId,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Partner name is required.');
        }

        return true;
    }

    static addFile = async (file) => {
        if (isNotNullOrUndefined(file)) {
            try {
                const fileName = uuid() + '.' + this.getFileExt(file);
                const storageRef = ref(storage, 'partners/' + fileName);

                return await uploadBytes(storageRef, file);
            } catch (err) {
                console.log(err);
            }
        }

        return null;
    }

    static getFileExt = (file) => {
        const lastDot = file.name.lastIndexOf('.');
        //const fileName = fullFileName.substring(0, lastDot);
        const ext = file.name.substring(lastDot + 1);


        return toLower(ext);
    }

    uploadImage = async () => {
        if (isNotNullOrUndefined(this.#image)) {
            const uploadRes = await Partner.addFile(this.#image);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                this.imageRefId = uploadRes.ref.fullPath;
            }
        }
    }

    uploadW9File = async () => {
        if (isNotNullOrUndefined(this.#file)) {
            const uploadRes = await Partner.addFile(this.#file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                this.w9FileRefId = uploadRes.ref.fullPath;
            }
        }
    }

    create = async () => {
        if (this.isValid()) {
            await this.uploadImage();
            await this.uploadW9File();

            return await create(Partner.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Partner.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedCampaign, oldCampaign) => {
        const updatedObj = new Partner(updatedCampaign);
        if (updatedObj.isValid() && id === updatedCampaign.id) {
            // update the image if a new image is provided
            if (isNotNullOrUndefined(updatedObj.#image)) {
                const uploadRes = await Partner.addFile(updatedObj.#image);
                // console.log(uploadRes);
                if (isObjectNotEmpty(uploadRes)) {
                    updatedObj.imageRefId = uploadRes.ref.fullPath;
                }
            }

            // update the w9 file if a new file is provided
            if (isNotNullOrUndefined(updatedObj.#file)) {
                const uploadRes = await Partner.addFile(updatedObj.#file);
                // console.log(uploadRes);
                if (isObjectNotEmpty(uploadRes)) {
                    updatedObj.w9FileRefId = uploadRes.ref.fullPath;
                }
            }

            const success = await update(Partner.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {

                return { ...oldCampaign, ...updatedObj };
            }
        } else {
            throw new Error('id and id field of the updated campaign must match.')
        }

        return null;
    }

    static updateImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Partner.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                let imageRefId = uploadRes.ref.fullPath;
                let success = await Partner.updateField(id, 'imageRefId', imageRefId);
                if (isBooleanTrue(success)) {
                    return imageRefId;
                }
            }
        }

        return null;
    }

    static updateW9File = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Partner.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                let w9FileRefId = uploadRes.ref.fullPath;
                let success = await Partner.updateField(id, 'w9FileRefId', w9FileRefId);
                if (isBooleanTrue(success)) {
                    return w9FileRefId;
                }
            }
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Partner.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Partner.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Partner.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Partner.collectionName, fieldName, fieldValue, comparer);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Partner.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { start, end } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, Partner.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { start, end } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Partner.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Partner.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Partner.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Partner;